// Add or remove a Wi-Fi network across a device, a room or a location.
//
// Same three-stage shape as UpdateCodeDialog — count, confirm, send — and for
// the same reason: this is not undoable and every device it reaches REBOOTS. It
// takes the same `scope` prop so the three entry points are identical to the
// ones already on the page.
//
// ⚠️ THE CONFIRM STEP IS NOT A FORMALITY HERE. A location-wide add restarts
// every device on the site at once. The operator sees the device list, the exact
// bytes, and how many are asleep, before anything is published.
//
// What the two buttons actually do is in core/devices/wifiFleet.js — including
// why an add also writes the location's record, why a remove cannot take that
// record back off, and why the parked string carries no `[uno]` and no
// `#userId`.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Alert, LinearProgress, Chip, Table, TableBody, TableRow, TableCell,
  Checkbox, CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import { useApp } from '../../core/state/AppContext';
import {
  collectRoomDevices, collectLocationDevices, splitByLiveness,
  pushWifi, queueWifi, saveLocationWifi, wifiPayload, whyNotAllowed,
} from '../../core/devices/wifiFleet';

const unoOf = (d) => d && (d.UniqueNumber ?? d.uniqueNumber);

export default function WifiFleetDialog({ open, scope, onClose }) {
  const { userId } = useApp();
  const [phase, setPhase] = useState('counting');   // counting|confirm|sending|done
  const [live, setLive] = useState([]);
  const [offline, setOffline] = useState([]);
  const [picked, setPicked] = useState({});         // uno -> boolean
  const [mode, setMode] = useState('add');
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(0);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setPhase('counting');
    setSent(0);
    setResult(null);
    let devices = [];
    if (scope?.type === 'device') devices = scope.device ? [scope.device] : [];
    else if (scope?.type === 'list') devices = scope.devices || [];
    else if (scope?.type === 'room') {
      devices = await collectRoomDevices(userId, scope.id, scope.locationId, scope.name);
    } else if (scope?.type === 'location') {
      devices = await collectLocationDevices(userId, scope.id);
    }
    const split = await splitByLiveness(devices);
    setLive(split.live);
    setOffline(split.offline);
    // Everything ticked by default: the operator opened this to change a site,
    // and un-ticking the exceptions is fewer clicks than ticking the rule.
    const all = {};
    [...split.live, ...split.offline].forEach((d) => { all[unoOf(d)] = true; });
    setPicked(all);
    setPhase('confirm');
  }, [scope, userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const chosen = (list) => list.filter((d) => picked[unoOf(d)]);
  const chosenLive = chosen(live);
  const chosenOffline = chosen(offline);
  const total = chosenLive.length + chosenOffline.length;

  const problem = whyNotAllowed(mode, ssid);
  const canSend = !problem && total > 0 && phase === 'confirm'
    && (mode === 'remove' || password.trim());

  const send = async () => {
    setPhase('sending');
    // The location's own record first: it is one row, it cannot reboot anything,
    // and if the operator closes the dialog mid-rollout it is the part worth
    // having kept. Only for an ADD — see saveLocationWifi.
    let recorded = false;
    const locId = scope?.type === 'location' ? scope.id : scope?.locationId;
    if (mode === 'add') recorded = await saveLocationWifi(locId, ssid.trim(), password);

    const queue = await queueWifi(chosenOffline, { mode, ssid: ssid.trim(), password });
    const pushed = await pushWifi(
      chosenLive,
      { mode, ssid: ssid.trim(), password },
      (n) => setSent(n),
    );
    setResult({ recorded, pushed, ...queue });
    setPhase('done');
  };

  const toggle = (d) => setPicked((p) => ({ ...p, [unoOf(d)]: !p[unoOf(d)] }));

  const row = (d, isLive) => (
    <TableRow key={unoOf(d)}>
      <TableCell padding="checkbox" sx={{ border: 0, py: 0 }}>
        <Checkbox size="small" checked={!!picked[unoOf(d)]} onChange={() => toggle(d)} />
      </TableCell>
      <TableCell sx={{ border: 0, py: 0, fontSize: 12 }}>
        {d.DeviceName || unoOf(d)}
        <Typography component="span" color="text.secondary" sx={{ fontSize: 10.5, ml: 0.75 }}>
          {unoOf(d)}
        </Typography>
      </TableCell>
      <TableCell sx={{ border: 0, py: 0, width: 92 }}>
        <Chip
          size="small"
          color={isLive ? 'success' : 'default'}
          variant={isLive ? 'filled' : 'outlined'}
          label={isLive ? 'now' : 'on its poll'}
          sx={{ height: 17, '& .MuiChip-label': { fontSize: 9.5, px: 0.75 } }}
        />
      </TableCell>
    </TableRow>
  );

  const scopeLabel = scope?.type === 'location' ? 'whole location'
    : scope?.type === 'room' ? 'room' : 'device';

  return (
    <Dialog open={open} onClose={phase === 'sending' ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <WifiIcon fontSize="small" />
        <span>{`Wi-Fi — ${scope?.name ? `${scope.name} · ` : ''}${scopeLabel}`}</span>
      </DialogTitle>

      <DialogContent>
        {phase === 'counting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Finding the devices and checking which are awake…</Typography>
          </Box>
        )}

        {phase !== 'counting' && (
          <>
            <ToggleButtonGroup
              size="small" exclusive value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ mb: 1 }}
              disabled={phase !== 'confirm'}
            >
              <ToggleButton value="add" sx={{ textTransform: 'none' }}>Add network</ToggleButton>
              <ToggleButton value="remove" sx={{ textTransform: 'none' }}>Remove network</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small" label="Network name (SSID)" value={ssid}
                onChange={(e) => setSsid(e.target.value)}
                sx={{ flex: 1 }} disabled={phase !== 'confirm'}
              />
              <TextField
                size="small" label="Password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ flex: 1 }} disabled={phase !== 'confirm'}
                // ⚠️ Needed for a REMOVE too. removeSsid matches the ssid AND
                // the password, so the wrong one silently erases nothing — the
                // original "REMOVEWIFI does nothing" bug.
                helperText={mode === 'remove'
                  ? 'Must match what is stored, or nothing is removed'
                  : ' '}
              />
            </Box>

            {problem && <Alert severity="warning" sx={{ py: 0, mb: 1 }}>{problem}</Alert>}

            {/* Said before sending, not after. */}
            <Alert severity="warning" sx={{ py: 0, mb: 1, '& .MuiAlert-message': { py: 0.5 } }}>
              <Typography variant="body2">
                {`Every device that accepts this RESTARTS. ${total} device${total === 1 ? '' : 's'}`}
                {chosenOffline.length > 0
                  ? ` — ${chosenLive.length} now, ${chosenOffline.length} on their next poll (~20 min).`
                  : ' will restart, one at a time.'}
              </Typography>
            </Alert>

            {mode === 'remove' && (
              <Alert severity="info" sx={{ py: 0, mb: 1, '& .MuiAlert-message': { py: 0.5 } }}>
                <Typography variant="body2">
                  This removes the network from the devices. The location keeps its
                  saved copy — there is no endpoint to delete that record.
                </Typography>
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Sends:
              {' '}
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                {wifiPayload(mode, ssid || '<ssid>', password || '<pass>', scope?.type === 'device' ? unoOf(scope.device) : null)}
              </Box>
            </Typography>

            <Box sx={{ maxHeight: 210, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableBody>
                  {live.map((d) => row(d, true))}
                  {offline.map((d) => row(d, false))}
                </TableBody>
              </Table>
            </Box>

            {phase === 'sending' && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={chosenLive.length ? (sent / chosenLive.length) * 100 : 100}
                />
                <Typography variant="caption" color="text.secondary">
                  {`Publishing ${sent} of ${chosenLive.length}…`}
                </Typography>
              </Box>
            )}

            {phase === 'done' && result && (
              <Alert severity="success" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                <Typography variant="body2">
                  {`Sent to ${result.pushed} now`}
                  {result.queued ? `, parked for ${result.queued} on their next poll` : ''}
                  {mode === 'add' ? (result.recorded ? ', and saved to the location.' : '.') : '.'}
                </Typography>
                {/* Named, not silently dropped — the operator has to know these
                    devices were skipped and why. */}
                {result.skipped?.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {`${result.skipped.length} left alone — already holding another queued command: `}
                    {result.skipped.slice(0, 4).map((s) => `${s.device.DeviceName || unoOf(s.device)} (${s.held})`).join(' · ')}
                  </Typography>
                )}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={phase === 'sending'}>
          {phase === 'done' ? 'Close' : 'Cancel'}
        </Button>
        {phase !== 'done' && (
          <Tooltip title={problem || (total === 0 ? 'Tick at least one device' : '')}>
            <span>
              <Button
                variant="contained"
                color={mode === 'remove' ? 'error' : 'primary'}
                onClick={send}
                disabled={!canSend}
              >
                {phase === 'sending'
                  ? <CircularProgress size={18} />
                  : `${mode === 'remove' ? 'Remove from' : 'Add to'} ${total}`}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
}
